'use client';

import { useWatch } from 'react-hook-form';

import { cn } from 'cn';
import { z } from 'zod';

import {
  isVoiceComposerActive,
  ProjectResponseSendFormRenderer,
  RendererOption,
} from '@/shared-project-response-message-renderers';
import {
  messageFormSchema,
  SubmitMessageOptions,
  useProjectResponseModeContext,
} from '@/shared-survey';

import { DEFAULT_RENDERER_TYPE } from './project-response-chat.helpers';
import ConversationOverMessageFormRenderer from './project-response-conversation-over-message-form-renderer';
import { useProjectResponseForm } from './project-response-form.hooks';

type MessageFormValues = z.infer<typeof messageFormSchema>;

interface Props {
  loading?: boolean;
  disabled?: boolean;
  onSubmit: (
    values: MessageFormValues,
    options?: SubmitMessageOptions
  ) => Promise<void>;
  questionType?: RendererOption | null;
  isConversationOver?: boolean;
  hideNewLineText?: boolean;
  onConversationOver?: () => void;
  renderMobileView?: boolean;
  conversationOverText?: string | null;
  isUserVoiceEnabled?: boolean;
  formGradientOuter?: string;
  formGradientInner?: string;
}

const PILL_RENDERER_TYPES = new Set<RendererOption>([
  'rating',
  'matrix',
  'ranking',
  'multipleChoice',
  'usabilityNext',
]);

export default function ProjectMessageForm({
  loading,
  disabled,
  onSubmit,
  questionType,
  isConversationOver,
  hideNewLineText,
  onConversationOver,
  renderMobileView,
  conversationOverText,
  isUserVoiceEnabled,
  formGradientOuter,
  formGradientInner,
}: Props) {
  const { responseMode } = useProjectResponseModeContext();

  const {
    register,
    submitOnEnter,
    isSubmitting,
    control,
    resetAndSubmit,
    handleSubmit,
  } = useProjectResponseForm({
    loading,
    disabled,
    questionType,
    onSubmit,
  });

  const content = useWatch({ control, name: 'content' });

  const resolvedType = questionType ?? DEFAULT_RENDERER_TYPE;
  const showConversationOver = Boolean(
    onConversationOver && isConversationOver
  );
  const useGradientPill =
    showConversationOver ||
    (!isVoiceComposerActive(responseMode, resolvedType) &&
      PILL_RENDERER_TYPES.has(resolvedType));

  const formContent =
    onConversationOver && isConversationOver ? (
      <ConversationOverMessageFormRenderer
        onClick={onConversationOver}
        conversationOverText={conversationOverText}
        type={resolvedType}
      />
    ) : (
      <ProjectResponseSendFormRenderer
        type={resolvedType}
        isSubmitting={isSubmitting}
        onKeyDown={submitOnEnter}
        disabled={disabled}
        register={register}
        value={content}
        hideNewLineText={hideNewLineText}
        isConversationOver={isConversationOver}
        renderMobileView={renderMobileView}
        onSubmit={onSubmit}
        isUserVoiceEnabled={isUserVoiceEnabled}
      />
    );

  return (
    <form
      onSubmit={handleSubmit((data) => resetAndSubmit(data))}
      className='mb-0 flex w-full items-center justify-stretch gap-2'
      data-submitting={isSubmitting}
      data-test='project-message-form'
    >
      {useGradientPill ? (
        <div
          className={cn(
            'w-full rounded-full bg-linear-to-r from-rose-400 to-indigo-400 p-px shadow-2xl sm:rounded-3xl',
            formGradientOuter
          )}
        >
          <div
            className={cn(
              'rounded-full bg-white p-2 sm:rounded-[23px]',
              formGradientInner
            )}
          >
            {formContent}
          </div>
        </div>
      ) : (
        formContent
      )}
    </form>
  );
}
